import React, { useContext, useEffect, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import useReportTemplateStyle from "./ReportTemplatePageStyle";
import useEnv from "../../../hooks/env";
import downloadFile from "../../../functions/downloadFile.function";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { IReportDownloadProps } from "./interfaces";
import TestReportDownloadApi from "../../../api/services/TestReportDownloadApi";
import { FILE_TYPES, FILE_TYPES_MAPPING, LANGUAGES, LANGUAGES_MAPPING } from "./common";

const ReportTemplateUploadDialog: React.FC<IReportDownloadProps> = (props) => {
  // API
  const { api } = useEnv();
  const testReportDownloadApi = new TestReportDownloadApi(api);
  // Props
  const { open, onClose, requestId } = props;
  // Context
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  // Styles
  const classes = useReportTemplateStyle(props);
  // State
  const [language, setLanguage] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [toggleDownloadButton, setToggleDownloadButton] = useState<boolean>(true);

  const resetState = () => {
    setFileType("");
    setLanguage("");
    setToggleDownloadButton(true);
  };

  const handleOnChangeLanguage = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setLanguage(e.target.value as string);
  };

  const handleOnChangeFileType = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setFileType(e.target.value as string);
  };

  const handleOnDownload = () => {
    if (!fileType || !language) {
      showSnackbar("Enter file type or language", "error");
      return;
    }
    const fileTypeMapped = FILE_TYPES_MAPPING[fileType];
    const languageMapped = LANGUAGES_MAPPING[language];
    testReportDownloadApi
      .downloadReport({ fileType: fileTypeMapped, language: languageMapped, requestId })
      .then((response) => {
        downloadFile(response);
        onClose();
      })
      .catch(handleHttpError);
  };

  useEffect(() => {
    resetState();
  }, [open]);

  useEffect(() => {
    if (fileType && language) {
      setToggleDownloadButton(false);
    }
  }, [fileType, language]);

  return (
    <CustomDialog onClose={onClose} open={open} disableBackdropClick>
      <div>
        <Grid container>
          <h2 className={classes.header}>Generate Report</h2>
          <Grid item xs={12} className={classes.textField}>
            <CustomDropdown
              mandatory
              name="language"
              id="language"
              type="language"
              labelText="Language"
              value={language}
              onChange={handleOnChangeLanguage}
            >
              {LANGUAGES.map((v: string) => (
                <MenuItem key={v} value={v}>
                  {v.toUpperCase()}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
          <Grid item xs={12} className={classes.textField}>
            <CustomDropdown
              mandatory
              name="fileType"
              id="fileType"
              labelText="File Type"
              value={fileType}
              onChange={handleOnChangeFileType}
            >
              {FILE_TYPES.map((v: string) => (
                <MenuItem key={v} value={v}>
                  {v.toUpperCase()}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Grid>
          <Grid className={classes.buttonContainer} xs={12} container item direction="row" justify="flex-end">
            <Box marginLeft={2}>
              <CustomButton text="Cancel" onClick={onClose} />
            </Box>
            <Box marginLeft={2}>
              <CustomButton primary text="Generate" onClick={handleOnDownload} disabled={toggleDownloadButton} />
            </Box>
          </Grid>
        </Grid>
      </div>
    </CustomDialog>
  );
};

export default ReportTemplateUploadDialog;
